import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import emoticonsData from '~/data/emoticons.json';
import styles from './MessageReactions.module.css';

const ITEMS_PER_PAGE = 50;
const PICKER_HEIGHT = 260;
const PICKER_WIDTH = 260;

interface MessageReactionsProps {
  messageId: string;
  roomId: string;
  reactions?: { [emojiId: string]: string[] };
  isMine: boolean;
  isHovered: boolean;
}

const EmojiPickerList = memo(({ active, onSelect }: { active: boolean; onSelect: (id: number) => void }) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const loaderRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, emoticonsData.length));
  }, []);

  useEffect(() => {
    if (!active) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [active, loadMore]);

  const visibleItems = (emoticonsData as { id: number; url: string }[]).slice(0, visibleCount);

  return (
    <>
      {visibleItems.map(item => (
        <button key={item.id} className={styles.pickerBtn} onClick={() => onSelect(item.id)}>
          <img src={`/img/emoticons/${item.url}`} alt="" className={styles.pickerBtnImg} loading="lazy" />
        </button>
      ))}
      {visibleCount < emoticonsData.length && (
        <div ref={loaderRef} style={{ height: '20px', width: '100%' }} />
      )}
    </>
  );
});

const MessageReactions = ({ messageId, roomId, reactions = {}, isMine, isHovered }: MessageReactionsProps) => {
  const { context } = useGlobalContext();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerCoords, setPickerCoords] = useState<{ top: number; left: number } | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const currentUserId = context.user?.id || '';
  const hasReactions = Object.values(reactions).some(users => users.length > 0);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (!pickerRef.current?.contains(target) && !addButtonRef.current?.contains(target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPicker]);

  const handleTogglePicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showPicker && addButtonRef.current) {
      const rect = addButtonRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const top = rect.top >= PICKER_HEIGHT + 8
        ? rect.top - PICKER_HEIGHT - 4
        : Math.min(rect.bottom + 4, vh - PICKER_HEIGHT - 4);
      const preferredLeft = isMine ? rect.right - PICKER_WIDTH : rect.left;
      const left = Math.max(4, Math.min(preferredLeft, vw - PICKER_WIDTH - 4));
      setPickerCoords({ top, left });
    }
    setShowPicker(prev => !prev);
  };

  const handleReact = (emojiId: string | number) => {
    window.dispatchEvent(new CustomEvent('addReaction', {
      detail: { messageId, roomId, emojiId: String(emojiId) }
    }));
    setShowPicker(false);
  };

  return (
    <div className={`${styles.reactionsArea} ${isMine ? styles.isMine : ''}`}>
      {hasReactions && (
        <div className={styles.reactionsList}>
          {Object.entries(reactions)
            .filter(([, users]) => users.length > 0)
            .map(([emojiId, users]) => (
              isMine ? (
                <span
                  key={emojiId}
                  className={styles.badge}
                  title={`${users.length}`}
                >
                  <img src={`/img/emoticons/${emojiId}.png`} alt="" className={styles.badgeImg} />
                  <span className={styles.badgeCount}>{users.length}</span>
                </span>
              ) : (
                <button
                  key={emojiId}
                  className={`${styles.badge} ${users.includes(currentUserId) ? styles.badgeActive : ''}`}
                  onClick={() => handleReact(emojiId)}
                  title={`${users.length}`}
                >
                  <img src={`/img/emoticons/${emojiId}.png`} alt="" className={styles.badgeImg} />
                  <span className={styles.badgeCount}>{users.length}</span>
                </button>
              )
            ))}
        </div>
      )}

      {!isMine && (
        <button
          ref={addButtonRef}
          className={`${styles.addBtn} ${(isHovered || showPicker || hasReactions) ? styles.addBtnVisible : ''}`}
          onClick={handleTogglePicker}
          title="Dodaj reakcję"
        >
          <span className={styles.addBtnIcon}>😊</span>
          <span className={styles.addBtnPlus}>+</span>
        </button>
      )}

      {showPicker && pickerCoords && ReactDOM.createPortal(
        <div
          ref={pickerRef}
          className={styles.picker}
          style={{ top: pickerCoords.top, left: pickerCoords.left }}
          onClick={e => e.stopPropagation()}
        >
          <EmojiPickerList active={showPicker} onSelect={handleReact} />
        </div>,
        document.body
      )}
    </div>
  );
};

export default MessageReactions;
